import { Button, Menu, MenuItem } from "@mui/material";
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import React, { ReactNode, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalContext } from "../../../Shared/Modal/Modal";
import { AnimatePresence, motion } from "framer-motion";

export type RowAction = {
    icon?: ReactNode,
    label: string,
    onClick?: () => void,
    withModal?: {
        title?: string,
        body?: string,
        onYes: () => void,
        onNo?: () => void
    }
};

type RowActionsProps = {
    actions: Array<RowAction>,
    small?: boolean,
};

export default function RowActions({ actions, small }: RowActionsProps) {
    const modalContext = useContext(ModalContext);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const open = Boolean(anchorEl);
    const { t } = useTranslation();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setHighlightedIndex(-1);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        event.preventDefault();

        const handleArrowDown = () => {
            setHighlightedIndex((prevIndex) => (prevIndex + 1) % actions.length);
        };

        const handleArrowUp = () => {
            if (highlightedIndex === -1) {
                setHighlightedIndex(actions.length);
            }
            setHighlightedIndex(
                (prevIndex) => (prevIndex - 1 + actions.length) % actions.length
            );
        };

        const handleEnter = () => {
            if (highlightedIndex >= 0) {
                playAction(actions[highlightedIndex]);
                setTimeout(() => {
                    handleClose();
                }, 100);
            }
        };

        const handleEscapeOrTab = () => {
            handleClose();
        };

        const keyHandlers: Record<string, () => void> = {
            ArrowDown: handleArrowDown,
            ArrowUp: handleArrowUp,
            Enter: handleEnter,
            Escape: handleEscapeOrTab,
            Tab: handleEscapeOrTab,
        };

        const handler = keyHandlers[event.key];
        if (!handler) return;
        handler();
    };

    const playAction = (action: RowAction): void => {
        if (modalContext && action.withModal) {
            modalContext.withModal(
                {
                    title: action.withModal.title || t("areYouSure"),
                    body: action.withModal.body || t("confirmDeletion"),
                },
                action.withModal.onYes || action.onClick,
                action.withModal.onNo
            )();

            handleClose();
            return;
        }

        if (action.onClick) {
            action.onClick();
        }

        handleClose();
    };

    return (
        <>
            <Button
                style={{ border: '1px solid #D7E3F2' }}
                className={`relative text-black py-1 rounded-[0.4rem] ${open ? 'bg-gray-300' : 'bg-white'}`}
                onClick={(e) => { e.stopPropagation(); handleClick(e); }}
            >
                <div className={`${small ? '' : 'px-6'} py-1 text-black`}>
                    {!small ? t('datagrid:actions') : null}
                    <FontAwesomeIcon className={!small ? 'pl-2' : ''} icon={faEllipsis} />
                </div>
                <AnimatePresence>
                    {open && <motion.div
                        initial={{ x: -50, rotate: 45 }}
                        animate={{ x: 0, rotate: 45 }}
                        exit={{ x: -50, rotate: 45 }}
                        transition={{ duration: 0.2 }}
                        className="w-5 h-5 bg-white border-[1px] border-[#D7E3F2] absolute -left-[22px]"
                    />}
                </AnimatePresence>
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        borderRadius: '0.4rem',
                        border: '1px solid #D7E3F2',
                        zIndex: 10,
                        ml: -1,
                    },
                }}
                onKeyDown={handleKeyDown}
                autoFocus={false}
            >
                {actions.map((action, index) => (
                    <MenuItem
                        key={action.label}
                        onClick={() => playAction(action)}
                        className={`h-8 px-5 my-1 ${highlightedIndex === index ? 'bg-blue-50' : ''}`}
                    >
                        <div className="flex items-center w-full border-b-[1px] border-[#dbdbdb]">
                            {action.icon && <span className="mx-2">{action.icon}</span>}
                            <div className="py-1 px-1">
                                {action.label}
                            </div>
                        </div>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
